import { ColumnsType, DeleteIcon, InfiniteTable } from '@/components/_common';
import {
  useDeleteWorkOrderCommission,
  useWorkOrderCommissions,
} from '@/core/domain/workorder/WorkOrdersCommisions/WorkOrdersCommissions.hooks';
import { WorkOrderCommission } from '@/core/domain/workorder/WorkOrdersCommisions/WorkOrdersCommissions.types';
import {
  WorkOrderItem,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useDisclosure } from '@/hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Button, Col, Modal, ModalProps, Row, Typography } from 'antd';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import CommissionCreateModalForm from '../CommissionCreateModalForm';
import Authorities from '@/types/Authority';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import formatterPrice from '@/utils/number/formatterPrice';
import formatterPercent from '@/utils/number/formatterPercent';
import { hasPermissions } from '@/utils/functions/hasPermissions';

type Props = ModalProps & {
  item?: WorkOrderItem;
  itemId?: number;
  quantity?: number;
  onlyList?: boolean;
  onRemoveSuccess?: VoidFunction;
};

const CommissionListModal: React.FC<Props> = ({
  item,
  itemId,
  quantity,
  onlyList,
  onRemoveSuccess,
  ...props
}) => {
  const [modalOpen, modalAction] = useDisclosure();

  const commissionRemove = useDeleteWorkOrderCommission();

  const {
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const { data, isLoading, hasNextPage, fetchNextPage } =
    useWorkOrderCommissions({
      complementId: complement?.id,
      itemId: itemId,
    });

  const commissions = flatInfiniteData(data);

  const commissionQuantity = commissions.reduce((prev, curr) => {
    return prev + curr.commissionQuantity;
  }, 0);

  function confirmRemove(commission: WorkOrderCommission) {
    Modal.confirm({
      title: 'Remover comissão',
      content: `Deseja realmente remover a commissão do funcionário "${commission.employee.name}"?`,
      onOk: () =>
        !!complement &&
        !!itemId &&
        commissionRemove
          .mutateAsync({
            complementId: complement?.id,
            itemId: itemId,
            employeeId: commission.employee.id,
          })
          .then(() => !!onRemoveSuccess && onRemoveSuccess()),
    });
  }

  const columns: ColumnsType<WorkOrderCommission> = [
    {
      key: 'employee',
      dataIndex: 'employee',
      title: 'Funcionário',
      render: (_, row) => row.employee.name,
      width: COLUMNS_WIDTH.default,
    },
    {
      key: 'commissionQuantity',
      dataIndex: 'commissionQuantity',
      title: 'Quantidade',
      align: 'right',
      width: COLUMNS_WIDTH.default,
    },
    {
      key: 'percent',
      dataIndex: 'percent',
      title: 'Comissão (%)',
      align: 'right',
      render: (value?: number) =>
        typeof value === 'number' ? formatterPercent(value / 100) : '-',
      width: COLUMNS_WIDTH.default,
    },
    {
      key: 'baseValue',
      dataIndex: 'baseValue',
      title: 'Valor base ($)',
      align: 'right',
      render: (value?: number) => (value ? formatterPrice(value) : '-'),
      width: COLUMNS_WIDTH.default,
    },
    {
      key: 'action',
      title: 'Ação',
      align: 'right',
      render: (_, row) => <DeleteIcon onClick={() => confirmRemove(row)} />,
      width: COLUMNS_WIDTH.default,
    },
  ];

  return (
    <Modal
      {...props}
      title="Comissão de funcionários"
      footer={false}
      width={900}
    >
      <Row align="middle" style={{ marginBottom: 24 }}>
        <Col flex={1}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Comissões
          </Typography.Title>
        </Col>

        {!onlyList && (
          <Col>
            <Button
              onClick={modalAction.open}
              disabled={
                commissionQuantity === quantity ||
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ADD_EMPLOYEE_TO_COMMISSION,
                ]) ||
                (complement?.status === WorkOrderComplementStatus.Closed &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_CLOSED_ADD_COMMISSION,
                  ])) ||
                (item?.type === WorkOrderItemType.Part &&
                  !item?.isInternal &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_ADD_COMMISSION_PART_SUPPLIED,
                  ])) ||
                (item?.type === WorkOrderItemType.Service &&
                  !item?.isInternal &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_ADD_COMMISSION_THIRD_SERVICE,
                  ]))
              }
            >
              Adicionar comissão
            </Button>
          </Col>
        )}
      </Row>

      <InfiniteTable<WorkOrderCommission>
        dataSource={commissions}
        rowKey={(_, i) => String(i)}
        loading={isLoading}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        columns={columns}
        scroll={{ x: true }}
      />

      {!onlyList && (
        <CommissionCreateModalForm
          open={modalOpen}
          onCancel={modalAction.close}
          item={item}
          maxQuantity={(quantity || 0) - commissionQuantity}
          employeesSelected={commissions}
        />
      )}
    </Modal>
  );
};

export default CommissionListModal;
